import type { EditorCollectionItemDragData } from '@/components/blocks/editor/collection/collection-dnd';
import {
  type AccordionButtonVariant,
  AccordionContent,
  AccordionHeader,
  type AccordionIconStyle,
  AccordionIconTrigger,
  AccordionItem,
} from '@/components/ui/accordion';
import { MutedText } from '@/components/ui/typography';
import type { CollectionCardConfig, CollectionItem } from '@/lib/blocks/collection/blocks';
import { resolveAccordionFields } from '@/lib/blocks/collection/blocks';
import { cn } from '@/lib/utils';
import { CollectionItemField, CollectionItemTextField } from '../item-fields';
import { SortableCollectionCardRoot } from './card-shell';

export type SortableAccordionCardProps = {
  item: CollectionItem;
  totalCount: number;
  sortIndex: number;
  collectionSortableGroup: string;
  collectionItemDragData: EditorCollectionItemDragData;
  cardConfig: CollectionCardConfig;
  showDividers?: boolean;
  iconStyle?: AccordionIconStyle;
  buttonVariant?: AccordionButtonVariant;
  disabled?: boolean;
  actionsDisabled?: boolean;
  onUpdate: (updated: CollectionItem, commit?: boolean) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onSelectionChange?: (selected: boolean) => void;
  primarySelected?: boolean;
};

export function SortableAccordionCard({
  item,
  totalCount,
  sortIndex,
  collectionSortableGroup,
  collectionItemDragData,
  cardConfig,
  showDividers = true,
  iconStyle = 'chevron',
  buttonVariant = 'filled',
  disabled,
  actionsDisabled,
  onUpdate,
  onDelete,
  onDuplicate,
  onSelectionChange,
  primarySelected,
}: SortableAccordionCardProps) {
  const { questionField, bodyFields } = resolveAccordionFields(cardConfig.fields);

  return (
    <SortableCollectionCardRoot
      item={item}
      totalCount={totalCount}
      sortIndex={sortIndex}
      collectionSortableGroup={collectionSortableGroup}
      collectionItemDragData={collectionItemDragData}
      disabled={disabled}
      actionsDisabled={actionsDisabled}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onSelectionChange={onSelectionChange}
      primarySelected={primarySelected}
      outerClassName={cn('w-full', showDividers && 'border-b last:border-b-0')}
    >
      <AccordionItem value={item.id} className="border-b-0">
        <AccordionHeader className="flex items-center gap-4 py-2">
          <div className="min-w-0 flex-1 text-sm font-medium">
            {questionField ? (
              <CollectionItemTextField
                value={item[questionField.slot]}
                onUpdate={(v, commit) => onUpdate({ ...item, [questionField.slot]: v }, commit)}
                dragDisabled={disabled}
                itemId={item.id}
                slot={questionField.slot}
              />
            ) : (
              <MutedText className="italic">No question field</MutedText>
            )}
          </div>
          <AccordionIconTrigger
            iconStyle={iconStyle}
            buttonVariant={buttonVariant}
            aria-label={
              questionField &&
              typeof item[questionField.slot] === 'string' &&
              item[questionField.slot]
                ? `Toggle: ${String(item[questionField.slot]).replace(/<[^>]*>/g, '')}`
                : 'Toggle accordion item'
            }
            onPointerDown={(e) => e.stopPropagation()}
          />
        </AccordionHeader>
        <AccordionContent>
          {bodyFields.length > 0 ? (
            bodyFields.map((field) => (
              <div key={field.slot} className="text-muted-foreground">
                <CollectionItemField
                  field={field}
                  value={item[field.slot]}
                  onUpdate={(v, commit) => onUpdate({ ...item, [field.slot]: v }, commit)}
                  dragDisabled={disabled}
                  itemId={item.id}
                />
              </div>
            ))
          ) : (
            <MutedText className="italic">No answer field</MutedText>
          )}
        </AccordionContent>
      </AccordionItem>
    </SortableCollectionCardRoot>
  );
}
